import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import flatpickr from "flatpickr";

export default class extends Controller {
  static targets = ["description", "form", "input", "date", "colorPicker"];
  static values = {
    deleteUrl: String,
    mode: String,
    url: String,
  };

  connect() {
    if (this.hasDateTarget) {
      this.flatpickr = flatpickr(this.dateTarget, {
        onChange: (_selectedDates, _dateStr, _instance) => {
          this.update().bind(this);
        },
      });
    }

    document.addEventListener("mousedown", this.handleOutsideClick.bind(this));
    this.isEditing = false;
  }

  disconnect() {
    document.removeEventListener(
      "mousedown",
      this.handleOutsideClick.bind(this),
    );

    if (this.flatpickr) {
      this.flatpickr.destroy();
    }
  }

  edit(event) {
    if (this.isEditing) return;

    if (
      event.target.tagName === "A" ||
      event.target.type === "checkbox" ||
      event.target.tagName === "LABEL"
    ) {
      return;
    }

    event.stopPropagation();
    this.descriptionTarget.classList.add("hidden");
    this.formTarget.classList.remove("hidden");
    this.element.classList.add("editing");
    this.element.classList.remove("cursor-move");
    this.inputTarget.focus();
    const length = this.inputTarget.value.length;
    this.inputTarget.setSelectionRange(length, length);
    this.isEditing = true;

    this.inputTarget.addEventListener(
      "mousedown",
      this.handleInputMousedown.bind(this),
    );
  }

  handleInputMousedown(event) {
    event.stopPropagation();
  }

  cancel() {
    this.formTarget.classList.add("hidden");
    this.descriptionTarget.classList.remove("hidden");
    this.element.classList.remove("editing");
    this.element.classList.add("cursor-move");
    this.isEditing = false;

    // Remove mousedown event listener from the input
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener(
        "mousedown",
        this.handleInputMousedown.bind(this),
      );
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.cancel();
    }
  }

  handleOutsideClick(event) {
    if (this.isEditing && !this.element.contains(event.target)) {
      this.cancel();
    }
  }

  delete(event) {
    event.preventDefault();
    const deleteUrl = new URL(this.deleteUrlValue, window.location.origin);

    if (confirm("Are you sure you want to delete this task?")) {
      fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html, application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      })
        .then((response) => response.text())
        .then((html) => {
          Turbo.renderStreamMessage(html);
        })
        .catch((error) => console.error("Error deleting task:", error));
    }
  }

  update(event) {
    if (typeof event === "function") {
      event.preventDefault();
    }

    const form = event?.target?.closest("form") || this.element;
    const formData = new FormData(form);
    const updateUrl = form.dataset.taskEditUrlValue;

    form.querySelectorAll("input, textarea, button").forEach((element) => {
      element.disabled = true;
    });

    // Check if this is a status update to "completed"
    const isCompletingTask = formData.get("task[status]") === "completed";
    // Store the click coordinates if this is a task completion
    const clickX = event?.clientX;
    const clickY = event?.clientY;
    // Get task color if available
    const taskColor = formData.get("task[color]") || "#4ade80"; // Default to green if no color

    fetch(updateUrl, {
      method: "PATCH",
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html, text/html, application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content,
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
        this.element.classList.remove("editing");
        this.element.classList.add("cursor-move");
        this.isEditing = false;
        // Remove mousedown event listener from the input
        if (this.hasInputTarget) {
          this.inputTarget.removeEventListener(
            "mousedown",
            this.handleInputMousedown.bind(this),
          );
        }

        form.querySelectorAll("input, textarea, button").forEach((element) => {
          element.disabled = false;
        });

        // Dispatch task:completed event if task was marked as completed
        if (isCompletingTask && clickX && clickY) {
          console.log("Dispatching task:completed event", {
            clickX,
            clickY,
            taskColor,
          });
          document.dispatchEvent(
            new CustomEvent("task:completed", {
              detail: {
                clientX: clickX,
                clientY: clickY,
                color: taskColor,
              },
            }),
          );
        }
      })
      .catch((error) => console.error("Error updating task:", error));
  }

  toggleCheckbox(event) {
    if (!this.hasInputTarget) {
      console.error("No input target found");
      return;
    }

    const checkboxInput = event.target;
    const checkboxIndex = parseInt(checkboxInput.dataset.checkboxIndex);
    const description = this.formTarget.querySelector("textarea").value;

    // Find the nth occurrence of the checkbox pattern
    let count = 0;
    const checkboxPattern = /\[([ x])\]/gi;
    const newText = description.replace(checkboxPattern, (match) => {
      count++;
      if (count === checkboxIndex) {
        return checkboxInput.checked ? "[x]" : "[ ]";
      }
      return match;
    });

    // Create and submit form data
    const formData = new FormData();
    formData.append("task[description]", newText);
    formData.append(
      "task[id]",
      this.formTarget.querySelector("input[name='task[id]']").value,
    );

    const updateUrl = this.formTarget.dataset.taskEditUrlValue;

    fetch(updateUrl, {
      method: "PATCH",
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html, text/html, application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content,
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
      })
      .catch((error) => console.error("Error updating checkbox:", error));
  }

  updateColor(event) {
    const color = event.target.dataset.color || event.target.value || "";
    const formData = new FormData();
    formData.append("task[color]", color);

    fetch(this.urlValue, {
      method: "PATCH",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
      })
      .catch((error) => console.error("Error updating color:", error));
  }
}
